import { useQuery } from '@tanstack/react-query';
import { type ReactNode, useMemo, useState } from 'react';
import { api, type WireLine } from '@/api/client';
import { clockIn, LEVEL_LABEL, lineTime, messageText, romeDay, zoneLabel, type Zone } from '@/lib/format';
import { renderText } from '@/lib/text';
import { Icon } from './Icon';

type Json = null | boolean | number | string | Json[] | { [key: string]: Json };
type Tab = 'stack' | 'attributes' | 'request' | 'context' | 'raw';

const isObject = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null && !Array.isArray(value);
const BITBUCKET = 'https://bitbucket.org/adoreasrl/bustard/src';
const CONTEXT_LINES = 12;
const LEVEL_WORD: Readonly<Record<string, string>> = {
    trace: 'Trace',
    debug: 'Debug',
    info: 'Info',
    warn: 'Warning',
    error: 'Error',
    fatal: 'Fatal',
};

export interface InspectorProps {
    line: WireLine;
    privacy: boolean;
    zone: Zone;
    onClose: () => void;
    onTerm: (field: string, value: string, exclude: boolean) => void;
    onQuery: (q: string) => void;
    onAround: (line: WireLine) => void;
    // Opens another line here (from the context).
    onSelect: (line: WireLine) => void;
    // The next or the previous line of the list (j, k).
    onStep: (direction: 1 | -1) => void;
    toast: (message: string) => void;
}

// The attributes that match a search, and the branches that lead to them.
function prune(value: Json, needle: string, path: string): Json | undefined {
    if (needle === '') {
        return value;
    }
    if (isObject(value) || Array.isArray(value)) {
        const entries = Array.isArray(value) ? value.map((item, index) => [String(index), item] as const) : Object.entries(value);
        const kept = entries.flatMap(([key, child]) => {
            const childPath = path ? `${path}.${key}` : key;
            if (childPath.toLowerCase().includes(needle)) {
                return [[key, child] as const];
            }
            const inner = prune(child as Json, needle, childPath);

            return inner === undefined ? [] : [[key, inner] as const];
        });
        if (kept.length === 0) {
            return undefined;
        }

        return Array.isArray(value) ? kept.map(([, child]) => child as Json) : (Object.fromEntries(kept) as Json);
    }

    return String(value).toLowerCase().includes(needle) ? value : undefined;
}

function JsonNode(props: {
    name: string;
    value: unknown;
    path: string[];
    privacy: boolean;
    secrets: string[];
    open: boolean;
    onTerm: InspectorProps['onTerm'];
    toast: InspectorProps['toast'];
}): ReactNode {
    const [open, setOpen] = useState(props.open);
    const { name, value, path } = props;
    if (isObject(value) || Array.isArray(value)) {
        const entries = Array.isArray(value) ? value.map((item, index) => [String(index), item] as const) : Object.entries(value);
        if (entries.length === 0) {
            return (
                <div className="jl">
                    <span className="jk">{name}</span>: <span className="jnull">{Array.isArray(value) ? '[]' : '{}'}</span>
                </div>
            );
        }

        return (
            <>
                <div className="jl">
                    <span className={`jk jtoggle ${open ? '' : 'closed'}`} onClick={() => setOpen(!open)}>
                        {name}
                    </span>
                    {!open && <span className="jsum"> {Array.isArray(value) ? `[${entries.length}]` : `{${entries.length}}`}</span>}
                </div>
                {open && (
                    <div className="jt">
                        {entries.map(([key, child]) => (
                            <JsonNode key={key} {...props} name={key} value={child} path={[...path, key]} open={entries.length <= 30} />
                        ))}
                    </div>
                )}
            </>
        );
    }
    const text = value === null || value === undefined ? 'null' : typeof value === 'string' ? JSON.stringify(value) : String(value);
    const className = value === null || value === undefined ? 'jnull' : typeof value === 'string' ? 'js' : typeof value === 'number' ? 'jn' : 'jb';
    const field = path.join('.');
    const raw = value === null || value === undefined ? '' : String(value);

    return (
        <div className="jl">
            <span className="jk">{name}</span>:{' '}
            <span className={className}>{renderText(text, { privacy: props.privacy, secrets: props.secrets })}</span>
            <span className="ja">
                <button title={`Keep ${field}:${raw}`} onClick={() => props.onTerm(field, raw, false)}>
                    +
                </button>
                <button title={`Exclude ${field}:${raw}`} onClick={() => props.onTerm(field, raw, true)}>
                    −
                </button>
                <button
                    title="Copy the value"
                    onClick={() => {
                        void navigator.clipboard.writeText(raw);
                        props.toast(`Copied the value of ${field}`);
                    }}
                >
                    ⧉
                </button>
                <button
                    title="Copy the path"
                    onClick={() => {
                        void navigator.clipboard.writeText(field);
                        props.toast(`Copied the path ${field}`);
                    }}
                >
                    .
                </button>
            </span>
        </div>
    );
}

interface Frame {
    fn: string;
    file: string;
    line: string;
    app: boolean;
    text: string;
}

function parseFrames(stack: string): Frame[] {
    return stack
        .split('\n')
        .slice(1)
        .map((frame) => frame.trim().replace(/^at /, ''))
        .filter(Boolean)
        .map((text) => {
            const match = /^(?:async )?(.*?) ?\(?(?:\/opt\/render\/project\/src\/)?([^()]*?):(\d+):(\d+)\)?$/.exec(text);

            return {
                fn: match?.[1] || '<anonymous>',
                file: match?.[2] ?? '',
                line: match?.[3] ?? '',
                app: text.includes('/apps/') && !text.includes('node_modules'),
                text,
            };
        });
}

// The stack as people read it: the app's frames, each with its file at the commit that ran on Bitbucket; the frames of
// libraries and Node folded in runs, one click away.
export function Stack(props: { exception: Record<string, unknown>; commit: string }): ReactNode {
    const stack = typeof props.exception.stack === 'string' ? props.exception.stack : '';
    const frames = useMemo(() => parseFrames(stack), [stack]);
    const [opened, setOpened] = useState<ReadonlySet<number>>(new Set());
    const runs: Array<{ app: boolean; start: number; frames: Frame[] }> = [];
    frames.forEach((frame, index) => {
        const last = runs.at(-1);
        if (last && last.app === frame.app) {
            last.frames.push(frame);
        } else {
            runs.push({ app: frame.app, start: index, frames: [frame] });
        }
    });

    return (
        <>
            <div className="stack-head">
                <div className="ex">{String(props.exception.name ?? props.exception.constructor ?? 'Error')}</div>
                <div className="exm">{String(props.exception.message ?? '')}</div>
            </div>
            {runs.map((run) =>
                run.app || opened.has(run.start) ? (
                    run.frames.map((frame, index) => {
                        // Compiled JavaScript (dist/…js) points back to its TypeScript source; its line is the compiled one.
                        const source = frame.file.replace(/\/dist\/src\//, '/src/').replace(/\.js$/, '.ts');

                        return (
                            <div key={run.start + index} className={`frame ${frame.app ? 'app' : 'lib'}`}>
                                <div>
                                    <div className="fn">{frame.fn}</div>
                                    <div className="loc">
                                        {frame.file ? `${frame.file.replace(/^apps\/backend\//, '')}:${frame.line}` : frame.text}
                                    </div>
                                </div>
                                {frame.app && props.commit && (
                                    <a href={`${BITBUCKET}/${props.commit}/${source}`} target="_blank" rel="noreferrer">
                                        Bitbucket @ {props.commit.slice(0, 7)} <Icon name="external" size={12} />
                                    </a>
                                )}
                            </div>
                        );
                    })
                ) : (
                    <button key={run.start} className="frames-folded" onClick={() => setOpened(new Set([...opened, run.start]))}>
                        + {run.frames.length} {run.frames.length === 1 ? 'frame' : 'frames'} in libraries and Node
                    </button>
                ),
            )}
            {frames.length === 0 && <div className="micro">No stack</div>}
        </>
    );
}

function Request(props: { request: Record<string, unknown>; privacy: boolean; secrets: string[]; toast: InspectorProps['toast'] }): ReactNode {
    const headers = isObject(props.request.headers) ? props.request.headers : {};
    const method = String(props.request.method ?? 'GET');
    const url = String(props.request.originalUrl ?? props.request.url ?? props.request.path ?? '');
    const host = String(headers.host ?? props.request.hostname ?? 'api.fleequid.com');
    const body = props.request.body;
    const curl = [
        `curl -X ${method} 'https://${host}${url}'`,
        ...Object.entries(headers)
            .filter(
                ([key, value]) =>
                    value &&
                    !['host', 'true-client-ip', 'rndr-id', 'x-forwarded-for', 'x-forwarded-proto', 'cf-connecting-ip', 'cf-ray'].includes(key),
            )
            .map(([key, value]) => `  -H '${key}: ${String(value)}'`),
        ...(isObject(body) && Object.keys(body).length > 0 ? [`  --data '${JSON.stringify(body)}'`] : []),
    ].join(' \\\n');

    return (
        <>
            <div className="req-line">
                <b>{method}</b> {renderText(url, { privacy: props.privacy, secrets: props.secrets })}
            </div>
            <table className="hdrs">
                <tbody>
                    {Object.entries(headers).map(([key, value]) => (
                        <tr key={key}>
                            <td>{key}</td>
                            <td>{renderText(String(value), { privacy: props.privacy, secrets: props.secrets })}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
            {isObject(body) && Object.keys(body).length > 0 && (
                <>
                    <div className="micro sub-title">Body</div>
                    <pre className="curl">{renderText(JSON.stringify(body, null, 2), { privacy: props.privacy, secrets: props.secrets })}</pre>
                </>
            )}
            <div style={{ marginTop: 12 }}>
                <button
                    className="act"
                    onClick={() => {
                        void navigator.clipboard.writeText(curl);
                        props.toast('cURL copied · obfuscated headers stay obfuscated');
                    }}
                >
                    Copy as cURL
                </button>
            </div>
            <pre className="curl">{renderText(curl, { privacy: props.privacy, secrets: props.secrets })}</pre>
        </>
    );
}

// What the same process wrote just before and just after: read in place, any line one click away.
function Context(props: {
    line: WireLine;
    zone: Zone;
    privacy: boolean;
    onSelect: (line: WireLine) => void;
    onAround: (line: WireLine) => void;
}): ReactNode {
    const { line, zone } = props;
    const context = useQuery({
        queryKey: ['context', line.env, line.id],
        queryFn: ({ signal }) => api.context(line.env, line.id, CONTEXT_LINES, signal),
        staleTime: 60_000,
    });
    if (context.isLoading) {
        return <span className="micro">Loading…</span>;
    }
    if (!context.data) {
        return <span className="micro">No context</span>;
    }
    const row = (item: WireLine, current: boolean): ReactNode => {
        const time = lineTime(item.ts, zone);

        return (
            <button key={item.id} className={`ctx-row lv-${item.level} ${current ? 'here' : ''}`} onClick={() => !current && props.onSelect(item)}>
                <span className="t">
                    {time.clock}
                    <span className="ms">{time.ms}</span>
                </span>
                <span className={`lv ${item.level}`}>{item.level === 'info' ? '' : (LEVEL_LABEL[item.level] ?? item.level)}</span>
                <span className="m">
                    {renderText(messageText(item.message), { privacy: props.privacy, secrets: item.user_id ? [item.user_id] : [] })}
                </span>
            </button>
        );
    };

    return (
        <div className="ctx">
            <div className="micro sub-title">
                {line.instance ? `Same instance · ${line.instance}` : `Same service · ${line.service}`} · {CONTEXT_LINES} lines before and after
            </div>
            {context.data.before.length === 0 && <div className="micro ctx-edge">Nothing before, within an hour</div>}
            {context.data.before.map((item) => row(item, false))}
            {row(line, true)}
            {context.data.after.map((item) => row(item, false))}
            {context.data.after.length === 0 && <div className="micro ctx-edge">Nothing after, within an hour</div>}
            <button className="act" style={{ marginTop: 14 }} onClick={() => props.onAround(line)}>
                Open ± 5 minutes in the list
            </button>
        </div>
    );
}

export function Inspector(props: InspectorProps): ReactNode {
    const needsFetch = props.line.attributes === undefined;
    const { data } = useQuery({
        queryKey: ['line', props.line.env, props.line.id],
        queryFn: () => api.line(props.line.env, props.line.id),
        enabled: needsFetch,
        staleTime: Infinity,
    });
    const line = needsFetch ? (data?.line ?? props.line) : props.line;
    const attributes = useMemo(() => (line.attributes ?? {}) as Record<string, Json>, [line.attributes]);
    const exception = isObject(attributes.exception) ? attributes.exception : null;
    const request = isObject(attributes.requestInfo) ? attributes.requestInfo : line.kind === 'request' && line.attributes ? attributes : null;
    const tabs: Array<[Tab, string]> = [
        ...(exception && typeof exception.stack === 'string' ? ([['stack', 'Stack']] as Array<[Tab, string]>) : []),
        ['attributes', 'Attributes'],
        ...(request ? ([['request', 'Request']] as Array<[Tab, string]>) : []),
        ['context', 'Context'],
        ['raw', 'Raw'],
    ];
    const [chosen, setChosen] = useState<Tab | null>(null);
    const [needle, setNeedle] = useState('');
    const tab = chosen && tabs.some(([key]) => key === chosen) ? chosen : (tabs[0]?.[0] ?? 'attributes');
    const time = lineTime(line.ts, props.zone);
    const other: Zone = props.zone === 'rome' ? 'utc' : 'rome';
    const secrets = line.user_id ? [line.user_id] : [];
    const privacy = props.privacy;
    const shownAttributes = useMemo(() => prune(attributes as Json, needle.trim().toLowerCase(), ''), [attributes, needle]);
    // An exception leads with its name and its message, the line as written under them.
    const exceptionName = exception ? String(exception.name ?? exception.constructor ?? line.exception ?? 'Error') : line.exception || null;
    const exceptionMessage = exception && exception.message !== undefined ? String(exception.message) : '';

    return (
        <aside className="inspector">
            <div className="ins-head">
                <div className="ins-top">
                    <span className={`sev ${line.level}`}>{LEVEL_WORD[line.level] ?? line.level}</span>
                    <span className="ins-time" title={`${clockIn(new Date(line.ts), other)} ${zoneLabel(other)}`}>
                        {romeDay(new Date(line.ts))} · {time.clock}
                        {line.ts.slice(19, 26)} {zoneLabel(props.zone)}
                    </span>
                    <span className="ins-nav">
                        <button title="Previous line (k)" onClick={() => props.onStep(-1)}>
                            <Icon name="up" size={16} />
                        </button>
                        <button title="Next line (j)" onClick={() => props.onStep(1)}>
                            <Icon name="down" size={16} />
                        </button>
                        <button className="ins-close" title="Close (esc)" onClick={props.onClose}>
                            <Icon name="x" size={17} />
                        </button>
                    </span>
                </div>
                <div className="ins-title">
                    {exceptionName && <h2>{exceptionName}</h2>}
                    {exceptionName && exceptionMessage && <p>{renderText(exceptionMessage, { privacy, secrets })}</p>}
                    <div className="ins-msg">{renderText(line.message, { privacy, secrets })}</div>
                </div>
                <dl className="kv">
                    <dt>Service</dt>
                    <dd>
                        <span className="link" onClick={() => props.onTerm('service', line.service, false)}>
                            {line.env}-bustard-{line.service}
                        </span>
                    </dd>
                    {line.instance && (
                        <>
                            <dt>Instance</dt>
                            <dd>
                                <span className="link" onClick={() => props.onTerm('instance', line.instance, false)}>
                                    {line.instance}
                                </span>
                            </dd>
                        </>
                    )}
                    {line.commit && (
                        <>
                            <dt>Commit</dt>
                            <dd>
                                <span className="link" onClick={() => props.onTerm('commit', line.commit, false)}>
                                    {line.commit.slice(0, 9)}
                                </span>{' '}
                                ·{' '}
                                <a className="link" href={`${BITBUCKET}/${line.commit}`} target="_blank" rel="noreferrer">
                                    Bitbucket ↗
                                </a>
                            </dd>
                        </>
                    )}
                    <dt>Logger</dt>
                    <dd>
                        <span className="link" onClick={() => props.onTerm('logger', line.logger, false)}>
                            {line.module} · {line.logger}
                        </span>
                    </dd>
                    {line.http_method && (
                        <>
                            <dt>Request</dt>
                            <dd>
                                {line.http_method} {renderText(line.http_path, { privacy, secrets })}
                                {line.http_status > 0 && <span className={`chip ${line.http_status >= 500 ? 'err' : ''}`}>{line.http_status}</span>}
                                {line.duration_ms > 0 && <span className="chip">{Math.round(line.duration_ms)} ms</span>}
                            </dd>
                        </>
                    )}
                    {line.user_id && (
                        <>
                            <dt>User</dt>
                            <dd>
                                <span className="link" onClick={() => props.onTerm('user', line.user_id, false)}>
                                    {privacy ? <span className="redact">████████████</span> : line.user_id}
                                </span>
                            </dd>
                        </>
                    )}
                    {line.error_uuid && (
                        <>
                            <dt>Exception</dt>
                            <dd>
                                <span
                                    className="link"
                                    title="Copy the exception UUID"
                                    onClick={() => {
                                        void navigator.clipboard.writeText(line.error_uuid);
                                        props.toast('Exception UUID copied');
                                    }}
                                >
                                    {line.error_uuid}
                                </span>
                            </dd>
                        </>
                    )}
                    {line.request_id && (
                        <>
                            <dt>Request id</dt>
                            <dd>
                                <span className="link" onClick={() => props.onTerm('request', line.request_id, false)}>
                                    {line.request_id}
                                </span>
                            </dd>
                        </>
                    )}
                </dl>
            </div>
            <div className="actions">
                {line.request_id && (
                    <button className="act primary" onClick={() => props.onQuery(`request:${line.request_id}`)} title="Every line of this request">
                        <Icon name="lines" size={15} /> Same request
                    </button>
                )}
                {line.user_id && (
                    <button className="act" onClick={() => props.onQuery(`user:${line.user_id}`)}>
                        <Icon name="user" size={15} /> Same user
                    </button>
                )}
                {line.fingerprint && (
                    <button className="act" onClick={() => props.onQuery(`fingerprint:${line.fingerprint}`)} title="Every occurrence of this error">
                        <Icon name="bolt" size={15} /> Same error
                    </button>
                )}
                {line.instance && (
                    <button className="act" onClick={() => props.onAround(line)} title="The list at ± 5 minutes, this instance only">
                        <Icon name="clock" size={15} /> ± 5 min
                    </button>
                )}
                <button
                    className="act"
                    title="Copy a link to this line (C)"
                    onClick={() => {
                        void navigator.clipboard.writeText(`${location.origin}/?env=${line.env}&line=${line.id}`);
                        props.toast('Link to this line copied');
                    }}
                >
                    <Icon name="link" size={15} /> Copy link
                </button>
            </div>
            <div className="tabs">
                {tabs.map(([key, label]) => (
                    <button key={key} className={tab === key ? 'on' : ''} onClick={() => setChosen(key)}>
                        {label}
                    </button>
                ))}
            </div>
            <div className="ins-body">
                {tab === 'stack' && exception && <Stack exception={exception} commit={line.commit} />}
                {tab === 'attributes' &&
                    (line.attributes ? (
                        <>
                            <input
                                className="attr-search"
                                placeholder="Search the attributes: a key, a path, a value"
                                value={needle}
                                onChange={(event) => setNeedle(event.target.value)}
                            />
                            {shownAttributes === undefined || (isObject(shownAttributes) && Object.keys(shownAttributes).length === 0) ? (
                                <div className="micro">{needle ? 'Nothing matches' : 'No attributes'}</div>
                            ) : (
                                Object.entries(shownAttributes as Record<string, Json>).map(([key, value]) => (
                                    <JsonNode
                                        key={`${key}-${needle}`}
                                        name={key}
                                        value={value}
                                        path={[key]}
                                        privacy={privacy}
                                        secrets={secrets}
                                        open={needle !== '' || !['requestInfo', 'request'].includes(key) || line.kind !== 'request'}
                                        onTerm={props.onTerm}
                                        toast={props.toast}
                                    />
                                ))
                            )}
                        </>
                    ) : (
                        <span className="micro">Loading…</span>
                    ))}
                {tab === 'request' && request && <Request request={request} privacy={privacy} secrets={secrets} toast={props.toast} />}
                {tab === 'context' && <Context line={line} zone={props.zone} privacy={privacy} onSelect={props.onSelect} onAround={props.onAround} />}
                {tab === 'raw' && (
                    <>
                        <button
                            className="act"
                            style={{ marginBottom: 10 }}
                            onClick={() => {
                                void navigator.clipboard.writeText(JSON.stringify(line, null, 2));
                                props.toast('The whole line copied as JSON');
                            }}
                        >
                            Copy JSON
                        </button>
                        <pre style={{ margin: 0, whiteSpace: 'pre-wrap', color: 'var(--ink-mid)' }}>
                            {renderText(JSON.stringify(line, null, 2), { privacy, secrets })}
                        </pre>
                    </>
                )}
            </div>
        </aside>
    );
}
